import { Ticket } from '../../types/ticket.types';

export type ManageTicketDisplayRow =
  | {
      kind: 'group';
      groupKey: string;
      groupLabel: string;
      rowTab: string;
      summary: {
        listingId: string;
        seatLabel: string;
        /** 'odd' / 'even' when every seat in the group is odd-only or even-only. */
        seatMode?: 'odd' | 'even' | null;
        totalQuantity: number;
        status: string;
        eventName: string;
        section: string;
        row: string;
      };
      tickets: Ticket[];
    }
  | {
      kind: 'ticket';
      ticket: Ticket;
      rowTab: string;
    };

export type ManageTicketDisplayInputRow =
  | ManageTicketDisplayRow
  | {
      kind: 'section';
      sectionKey: string;
      label: string;
      count: number;
    };

export type EventSearchGroupedTickets = Record<'pending' | 'listed' | 'sold' | 'delisted' | 'deleted', Ticket[]>;

export type EventSearchSectionConfig = Record<'pending' | 'listed' | 'sold' | 'delisted' | 'deleted', string>;

export const buildEventSearchManageTicketDisplayRows = (
  sectionedTickets: EventSearchGroupedTickets,
  sectionTabs: EventSearchSectionConfig,
  boundaryCandidates?: Ticket[],
): ManageTicketDisplayInputRow[] => {
  const rows: ManageTicketDisplayInputRow[] = [];
  const sectionOrder: Array<keyof EventSearchGroupedTickets> = ['pending', 'listed', 'sold', 'delisted', 'deleted'];
  const allStatusTickets =
    boundaryCandidates ?? sectionOrder.flatMap((sectionKey) => sectionedTickets[sectionKey] ?? []);

  sectionOrder.forEach((sectionKey) => {
    const tickets = sectionedTickets[sectionKey] ?? [];
    const label =
      sectionKey === 'pending'
        ? 'Pending'
        : sectionKey === 'listed'
          ? 'Listed'
          : sectionKey === 'sold'
            ? 'Sold'
            : sectionKey === 'delisted'
              ? 'Delisted'
              : 'Deleted';
    rows.push({ kind: 'section', sectionKey, label, count: tickets.length });
    rows.push(...buildManageTicketDisplayGroups(
      tickets,
      sectionTabs[sectionKey],
      allStatusTickets,
    ));
  });

  return rows;
};

export const getEventGroupKey = (ticket: Ticket): string | null => {
  const normalizeId = (value: unknown): string => {
    const id = String(value ?? '').trim();
    return id && !/^(null|undefined)$/i.test(id) ? id : '';
  };
  const isUsableListingId = (id: string) => Boolean(id) && !/^qa-/i.test(id);
  const vlListingId = normalizeId(ticket?.tp_vl_listing_id);
  const sourceTicketId = normalizeId(ticket?.t_id);
  const withTicketGroup = (prefix: string, listingId: string) =>
    sourceTicketId ? `${prefix}::${listingId}::ticket::${sourceTicketId}` : `${prefix}::${listingId}`;

  // Same group only when listing id AND t_id match. Different t_id or listing
  // id must stay separate (including two events that wrongly share a VL listing).
  if (isUsableListingId(vlListingId)) {
    return withTicketGroup('vl-listing', vlListingId);
  }

  const sourceListingId = [ticket?.tp_listing_id, ticket?.listing_id, ticket?.listingId]
    .map(normalizeId)
    .find(isUsableListingId);
  if (sourceListingId) {
    return withTicketGroup('listing', sourceListingId);
  }

  if (!sourceTicketId) return null;

  const eventId = normalizeId(ticket?.e_id) || normalizeId(ticket?.e_name);
  const section = normalizeId(ticket?.tp_section);
  const row = normalizeId(ticket?.tp_row);
  return `source-ticket::${JSON.stringify([sourceTicketId, eventId, section, row])}`;
};

const getSeatValue = (ticket: Ticket): string | null => {
  if (ticket?.tp_seat_no == null || ticket?.tp_seat_no === '') return null;
  return String(ticket.tp_seat_no).trim();
};

const getSeatSortValue = (ticket: Ticket): number => {
  const seatNumbers = parseSeatNumbers(getSeatValue(ticket))
    .map((seat) => Number(seat))
    .filter((seat) => Number.isFinite(seat));

  if (seatNumbers.length === 0) {
    return Number.MAX_SAFE_INTEGER;
  }

  return Math.min(...seatNumbers);
};

const sortTicketsBySeatNumber = (tickets: Ticket[]): Ticket[] => {
  return [...tickets].sort((a, b) => {
    const aValue = getSeatSortValue(a);
    const bValue = getSeatSortValue(b);
    if (aValue !== bValue) {
      return aValue - bValue;
    }
    return String(a?.tp_id ?? '').localeCompare(String(b?.tp_id ?? ''));
  });
};

/**
 * Before a Victory Live listing exists, seats that share a ticket/section/row
 * are still separate groups when the seat numbers are not consecutive.
 * 670–671 and 674–675 must not collapse into 670–675.
 */
const splitIntoContiguousSeatRuns = (tickets: Ticket[]): Ticket[][] => {
  const sorted = sortTicketsBySeatNumber(tickets);
  const runs: Ticket[][] = [];
  sorted.forEach((ticket) => {
    const seat = getSeatSortValue(ticket);
    const current = runs[runs.length - 1];
    const previous = current ? getSeatSortValue(current[current.length - 1]) : null;
    // Odd-only / even-only seats (1, 3, 5) are one run: the gap of 2 is expected when both seats share the mode.
    const previousTicket = current ? current[current.length - 1] : null;
    const sharedMode =
      previousTicket && ticket.tp_seat_mode && previousTicket.tp_seat_mode === ticket.tp_seat_mode
        ? ticket.tp_seat_mode
        : null;
    const maxStep = sharedMode ? 2 : 1;
    const continuesRun =
      current &&
      Number.isFinite(seat) &&
      previous != null &&
      Number.isFinite(previous) &&
      seat >= previous &&
      seat <= previous + maxStep;
    if (continuesRun && current) {
      current.push(ticket);
      return;
    }
    runs.push([ticket]);
  });
  return runs;
};

const parseSeatNumbers = (seatNo: string | number | null | undefined): string[] => {
  if (seatNo == null || seatNo === '') return [];
  const raw = String(seatNo).trim();
  if (!raw) return [];
  const pieces = raw.split(',').map((part) => part.trim()).filter(Boolean);
  const normalized = pieces.flatMap((part) => part.split(/[\s-–]+/).filter(Boolean));
  return normalized.filter(Boolean);
};

export const splitTicketsAtSeatBoundaries = <TActive, TBoundary>(
  activeTickets: TActive[],
  boundaryTickets: TBoundary[],
  getActiveSeatNo: (ticket: TActive) => string | number | null | undefined,
  getBoundarySeatNo: (ticket: TBoundary) => string | number | null | undefined,
): TActive[][] => {
  if (!activeTickets.length) return [];
  const activeBounds = activeTickets.map((ticket) => {
    const seats = parseSeatNumbers(getActiveSeatNo(ticket)).map(Number).filter(Number.isFinite);
    return seats.length ? { start: Math.min(...seats), end: Math.max(...seats) } : null;
  });
  if (activeBounds.some((bounds) => bounds === null)) return [activeTickets];

  const boundaryStarts = boundaryTickets
    .map((ticket) => {
      const seats = parseSeatNumbers(getBoundarySeatNo(ticket)).map(Number).filter(Number.isFinite);
      return seats.length ? Math.min(...seats) : null;
    })
    .filter((seat): seat is number => seat !== null);
  if (!boundaryStarts.length) return [activeTickets];

  const groups: TActive[][] = [];
  let currentGroup: TActive[] = [activeTickets[0]];
  for (let index = 1; index < activeTickets.length; index += 1) {
    const previousBounds = activeBounds[index - 1]!;
    const currentBounds = activeBounds[index]!;
    const hasBoundaryBetween = boundaryStarts.some(
      (seat) => seat > previousBounds.end && seat <= currentBounds.start,
    );
    if (hasBoundaryBetween) {
      groups.push(currentGroup);
      currentGroup = [];
    }
    currentGroup.push(activeTickets[index]);
  }
  groups.push(currentGroup);
  return groups;
};

const createSeatIds = (ticket: Ticket): string[] => {
  const seatNumbers = parseSeatNumbers(ticket?.tp_seat_no);
  return seatNumbers.map((_, index) => `${ticket.tp_id}-seat-${index}`);
};

export const resolveSelectedTicketIdsFromSeatSelection = (
  selectedSeatIds: string[],
  rows: ManageTicketDisplayInputRow[],
  expandedGroupKeys: string[] = [],
  expandedTicketIds: string[] = [],
): string[] => {
  void expandedGroupKeys;
  void expandedTicketIds;

  const selectedSeatSet = new Set(selectedSeatIds.filter(Boolean));
  const ticketIds = new Set<string>();

  rows.forEach((row) => {
    if (row.kind === 'group') {
      row.tickets.forEach((ticket) => {
        createSeatIds(ticket).forEach((seatId) => {
          if (selectedSeatSet.has(seatId) && ticket?.tp_id) {
            ticketIds.add(ticket.tp_id);
          }
        });
      });
      return;
    }

    if (row.kind === 'ticket') {
      createSeatIds(row.ticket).forEach((seatId) => {
        if (selectedSeatSet.has(seatId) && row.ticket?.tp_id) {
          ticketIds.add(row.ticket.tp_id);
        }
      });
    }
  });

  return Array.from(ticketIds);
};

/** 'odd' / 'even' when every ticket in the group carries the same setting. */
export const getGroupSeatMode = (tickets: Ticket[]): 'odd' | 'even' | null => {
  const first = tickets[0]?.tp_seat_mode ?? null;
  return first && tickets.every((ticket) => ticket.tp_seat_mode === first) ? first : null;
};

const createSeatLabel = (tickets: Ticket[]): string => {
  const seats = tickets.flatMap((ticket) => parseSeatNumbers(getSeatValue(ticket))).filter(Boolean);
  if (seats.length === 0) {
    return 'N/A';
  }
  const sorted = [...seats].sort((a, b) => Number(a) - Number(b));
  // Odd-only / even-only seats read as the seats themselves ("1, 3, 5"), not a range that implies every number.
  if (getGroupSeatMode(tickets) && sorted.length > 1) {
    return Array.from(new Set(sorted)).join(', ');
  }
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (sorted.length === 1) {
    return first;
  }
  return `${first}–${last}`;
};

export const getManageTicketDisplayTickets = (rows: ManageTicketDisplayInputRow[]): Ticket[] => {
  return rows.flatMap((row) => {
    if (row.kind === 'group') {
      return row.tickets;
    }
    if (row.kind === 'ticket') {
      return [row.ticket];
    }
    return [];
  });
};

/**
 * Expand a selection to every seat in the same Manage Tickets display group(s).
 * Add PID / remap must run on the full listing group, not one expanded seat.
 */
export const expandTicketsToDisplayGroups = (
  selected: Ticket[],
  rows: ManageTicketDisplayInputRow[],
): Ticket[] => {
  if (!selected.length) return [];
  const selectedIds = new Set(
    selected.map((ticket) => String(ticket?.tp_id || '').trim()).filter(Boolean),
  );
  if (!selectedIds.size) return selected;

  const expanded: Ticket[] = [];
  const seen = new Set<string>();

  rows.forEach((row) => {
    if (row.kind === 'group') {
      const hitsGroup = row.tickets.some((ticket) => selectedIds.has(String(ticket?.tp_id || '').trim()));
      if (!hitsGroup) return;
      row.tickets.forEach((ticket) => {
        const tpId = String(ticket?.tp_id || '').trim();
        if (!tpId || seen.has(tpId)) return;
        seen.add(tpId);
        expanded.push(ticket);
      });
      return;
    }

    if (row.kind === 'ticket') {
      const tpId = String(row.ticket?.tp_id || '').trim();
      if (!tpId || !selectedIds.has(tpId) || seen.has(tpId)) return;
      seen.add(tpId);
      expanded.push(row.ticket);
    }
  });

  return expanded.length ? expanded : selected;
};

/**
 * Rebuild display groups from a flat ticket list, then expand the selection
 * to every seat in the matching group(s). Prefer this when table rows may be
 * paginated/incomplete.
 */
export const expandTicketsToSameDisplayGroup = (
  selected: Ticket[],
  allTickets: Ticket[],
  rowTab = 'Listed',
): Ticket[] => {
  if (!selected.length) return [];
  const byId = new Map<string, Ticket>();
  [...(allTickets || []), ...selected].forEach((ticket) => {
    const tpId = String(ticket?.tp_id || '').trim();
    if (tpId) byId.set(tpId, ticket);
  });
  const rows = buildManageTicketDisplayGroups(Array.from(byId.values()), rowTab);
  return expandTicketsToDisplayGroups(selected, rows);
};

export const toggleExpandedGroupKeys = (expandedGroupKeys: string[], groupKey: string): string[] => {
  const isExpanded = expandedGroupKeys.includes(groupKey);
  if (isExpanded && expandedGroupKeys.length === 1) {
    return [];
  }
  return [groupKey];
};

export const getEditSeatRangeFormValues = (seatValue: string | number | null | undefined) => {
  const rawValue = seatValue == null ? '' : String(seatValue).trim();

  if (!rawValue) {
    return { startSeat: '', endSeat: '' };
  }

  const normalized = rawValue.replace(/\s/g, '');
  if (normalized.includes('-')) {
    const [startSeat, endSeat] = normalized.split('-');
    return {
      startSeat: startSeat || '',
      endSeat: endSeat || '',
    };
  }

  const [firstSeat] = normalized.split(',');
  return {
    startSeat: firstSeat || '',
    endSeat: '',
  };
};

export const getEffectiveSeatNumberFromRange = (seatValue: string | number | null | undefined): number | null => {
  const rawValue = seatValue == null ? '' : String(seatValue).trim();
  if (!rawValue) return null;

  const normalized = rawValue.replace(/\s/g, '');
  if (normalized.includes('-')) {
    const [startSeat] = normalized.split('-');
    const parsed = Number(startSeat);
    return Number.isFinite(parsed) ? parsed : null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getSoldTicketGroupingStatusKey = (
  paymentStatus: unknown,
  payoutStatus: unknown,
): string => {
  if (String(payoutStatus ?? '').trim() === 'Voided_Payout') return 'voidedpayout';
  if (String(paymentStatus ?? '').trim() === 'Success') return 'settled';
  if (String(paymentStatus ?? '').trim() === 'Inprogress') return 'settlementinprogress';
  if (String(paymentStatus ?? '').trim() === 'Failed') return 'failed';
  return 'sold';
};

export const getGroupingStatusKey = (ticket: Ticket): string => {
  const statusKey = String(ticket?.tp_status ?? '').trim().toLowerCase().replace(/\s+/g, '');
  if (statusKey !== 'sold') return statusKey;
  return getSoldTicketGroupingStatusKey(ticket.tp_payment_status, ticket.tp_payout_status);
};

export const buildManageTicketDisplayGroups = (
  tickets: Ticket[],
  rowTab = 'Listed',
  boundaryCandidates: Ticket[] = [],
): ManageTicketDisplayRow[] => {
  const groups = new Map<string, Ticket[]>();
  const baseKeyByGroup = new Map<string, string>();
  const statusKeysByBase = new Map<string, Set<string>>();
  const appearanceOrder: string[] = [];

  tickets.forEach((ticket) => {
    const baseKey = getEventGroupKey(ticket);
    // Preserve caller sort order (latest event activity first) across both
    // multi-seat groups and single-seat rows.
    if (!baseKey) {
      const soloKey = `__solo__${appearanceOrder.length}`;
      groups.set(soloKey, [ticket]);
      appearanceOrder.push(soloKey);
      return;
    }
    const statusKey = getGroupingStatusKey(ticket);
    const groupKey = JSON.stringify([baseKey, statusKey]);
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
      baseKeyByGroup.set(groupKey, baseKey);
      const statusKeys = statusKeysByBase.get(baseKey) ?? new Set<string>();
      statusKeys.add(statusKey);
      statusKeysByBase.set(baseKey, statusKeys);
      appearanceOrder.push(groupKey);
    }
    groups.get(groupKey)!.push(ticket);
  });

  return appearanceOrder.flatMap((key) => {
    const groupTickets = groups.get(key) || [];
    if (key.startsWith('__solo__') || groupTickets.length <= 1) {
      return [{
        kind: 'ticket' as const,
        ticket: groupTickets[0],
        rowTab,
      }];
    }

    const sortedGroupTickets = sortTicketsBySeatNumber(groupTickets);
    const baseKey = baseKeyByGroup.get(key) ?? key;
    const runs = baseKey.startsWith('source-ticket::')
      ? splitIntoContiguousSeatRuns(sortedGroupTickets)
      : [sortedGroupTickets];
    const activeStatusKey = getGroupingStatusKey(sortedGroupTickets[0]);
    const groupBoundaries = boundaryCandidates.filter(
      (ticket) =>
        getEventGroupKey(ticket) === baseKey &&
        getGroupingStatusKey(ticket) !== activeStatusKey,
    );
    const boundaryRuns = runs.flatMap((runTickets) =>
      splitTicketsAtSeatBoundaries(
        runTickets,
        groupBoundaries,
        (ticket) => ticket.tp_seat_no,
        (ticket) => ticket.tp_seat_no,
      ),
    );
    return boundaryRuns.map((runTickets) => {
      if (runTickets.length <= 1) {
        return {
          kind: 'ticket' as const,
          ticket: runTickets[0],
          rowTab,
        };
      }

      const first = runTickets[0];
      const seatLabel = createSeatLabel(runTickets);
      const groupStatusKey = getGroupingStatusKey(first);
      const statusSuffix = (statusKeysByBase.get(baseKey)?.size ?? 0) > 1
        ? `::status-${groupStatusKey || 'unknown'}`
        : '';
      const seatSuffix = boundaryRuns.length > 1 ? `::seats-${seatLabel}` : '';
      const seatMode = getGroupSeatMode(runTickets);
      const modeSuffix = seatMode ? `::seat-mode-${seatMode}` : '';
      const displayGroupKey = `${baseKey}${statusSuffix}${seatSuffix}${modeSuffix}`;
      return {
        kind: 'group' as const,
        groupKey: displayGroupKey,
        groupLabel: first.e_name || baseKey,
        rowTab,
        summary: {
          listingId: String(first?.tp_vl_listing_id ?? first?.listing_id ?? first?.listingId ?? first?.tp_listing_id ?? '').trim() || baseKey,
          seatLabel,
          seatMode,
          totalQuantity: runTickets.length,
          status: first.tp_status || 'Unknown',
          eventName: first.e_name || '-',
          section: first.tp_section || 'N/A',
          row: first.tp_row || 'N/A',
        },
        tickets: runTickets,
      };
    });
  });
};

/**
 * Keep seats that belong to the first `maxGroups` display rows only.
 * Used after an oversampled server page fetch so "Rows per page" means
 * event/listing rows, not individual seats.
 */
export const takeTicketsForFirstDisplayGroups = (
  tickets: Ticket[],
  maxGroups: number,
  rowTab = 'Listed',
): Ticket[] => {
  if (!tickets.length || maxGroups <= 0) return [];
  const groups = buildManageTicketDisplayGroups(tickets, rowTab);
  return getManageTicketDisplayTickets(groups.slice(0, maxGroups));
};

/** How many seats to request so a page can usually fill `pageSize` grouped rows. */
export const getGroupedPageSeatFetchSize = (pageSize: number): number => {
  const SEATS_PER_ROW_BUDGET = 40;
  const MAX_SEATS_PER_REQUEST = 400;
  const size = Math.max(pageSize, pageSize * SEATS_PER_ROW_BUDGET);
  return Math.min(MAX_SEATS_PER_REQUEST, size);
};

/**
 * Seat-window index for multi-window server paging.
 * A full fetch often contains many more display groups than one page (pairs are
 * ~2 seats, not the 40-seat budget). Page inside that window before requesting
 * the next seat offset.
 */
export const getGroupedSeatWindowIndex = (
  pageOffset: number,
  groupsPerFullWindow: number | null | undefined,
): number => {
  if (!groupsPerFullWindow || groupsPerFullWindow <= 0) return 0;
  return Math.max(0, Math.floor(Math.max(pageOffset, 0) / groupsPerFullWindow));
};

/**
 * Group offset inside the seat window that contains `pageOffset`.
 * Until a full window has been measured, the loaded fetch is window 0, so the
 * page offset itself is the slice start. Returning 0 here repeats the first page.
 */
export const getGroupedWindowLocalOffset = (
  pageOffset: number,
  groupsPerFullWindow: number | null | undefined,
): number => {
  const offset = Math.max(0, pageOffset);
  if (!groupsPerFullWindow || groupsPerFullWindow <= 0) return offset;
  const windowIndex = getGroupedSeatWindowIndex(offset, groupsPerFullWindow);
  return offset - windowIndex * groupsPerFullWindow;
};

/**
 * Next row offset after the current page.
 * A short page at the end of a seat window must land on the next window start.
 * Adding `pageSize` there skips the first groups of that window.
 */
export const getNextGroupedPageOffset = (args: {
  pageOffset: number;
  pageSize: number;
  displayedRows: number;
  groupsPerFullWindow: number | null | undefined;
}): number => {
  const pageOffset = Math.max(0, args.pageOffset);
  const pageSize = Math.max(1, args.pageSize);
  const step = args.displayedRows > 0 ? args.displayedRows : pageSize;
  const next = pageOffset + step;
  const perWindow = args.groupsPerFullWindow;
  if (!perWindow || perWindow <= 0) return next;
  const currentWindow = getGroupedSeatWindowIndex(pageOffset, perWindow);
  const nextWindow = getGroupedSeatWindowIndex(next, perWindow);
  if (nextWindow > currentWindow) return nextWindow * perWindow;
  return next;
};

/**
 * Row total for server tabs that cannot load every seat in one request.
 * Full windows extrapolate from observed seats-per-group. A short final window
 * is exact: prior full windows plus the groups in that remainder.
 */
export const resolveServerGroupedRowTotal = (args: {
  seatTotal: number;
  loadedSeats: number;
  loadedGroups: number;
  fetchSize: number;
  groupsPerFullWindow: number | null | undefined;
  windowIndex: number;
}): number => {
  const seatTotal = Math.max(0, args.seatTotal);
  const loadedSeats = Math.max(0, args.loadedSeats);
  const loadedGroups = Math.max(0, args.loadedGroups);
  const fetchSize = Math.max(1, args.fetchSize);
  if (loadedGroups <= 0) return 0;
  if (seatTotal <= 0 || loadedSeats >= seatTotal) return loadedGroups;

  const perWindow =
    args.groupsPerFullWindow && args.groupsPerFullWindow > 0
      ? args.groupsPerFullWindow
      : loadedGroups;
  const onPartialWindow = loadedSeats > 0 && loadedSeats < fetchSize;
  if (onPartialWindow) {
    return Math.max(0, args.windowIndex) * perWindow + loadedGroups;
  }
  return Math.max(loadedGroups, Math.round((perWindow * seatTotal) / fetchSize));
};

/**
 * Group total once the loaded window is the last seat window.
 * Returns null while another 400-seat window still exists, so the footer
 * does not turn the seat count into an extra page.
 */
export const resolveLoadedGroupedRowTotal = (args: {
  seatTotal: number;
  loadedSeats: number;
  loadedGroups: number;
  fetchSize: number;
  groupsPerFullWindow: number | null | undefined;
  windowIndex: number;
}): number | null => {
  const seatTotal = Math.max(0, args.seatTotal);
  const loadedSeats = Math.max(0, args.loadedSeats);
  const loadedGroups = Math.max(0, args.loadedGroups);
  const fetchSize = Math.max(1, args.fetchSize);
  if (loadedGroups <= 0) return 0;

  const windowIndex = Math.max(0, args.windowIndex);
  const moreSeatWindows = seatTotal > (windowIndex + 1) * fetchSize && loadedSeats >= fetchSize;
  if (moreSeatWindows) return null;
  if (seatTotal <= 0 || loadedSeats >= seatTotal) return loadedGroups;

  const perWindow =
    args.groupsPerFullWindow && args.groupsPerFullWindow > 0
      ? args.groupsPerFullWindow
      : loadedGroups;
  return windowIndex * perWindow + loadedGroups;
};

/**
 * True when the next click still has groups to show.
 * A short page at the end of the loaded groups is the last page only when
 * no later seat window exists. The rows on that short page stay visible.
 */
export const hasAnotherGroupedPage = (args: {
  pageOffset: number;
  displayedRows: number;
  loadedGroups: number;
  groupsPerFullWindow: number | null | undefined;
  moreSeatWindows: boolean;
}): boolean => {
  if (args.displayedRows <= 0) return false;
  const localOffset = getGroupedWindowLocalOffset(args.pageOffset, args.groupsPerFullWindow);
  if (localOffset + args.displayedRows < Math.max(0, args.loadedGroups)) return true;
  return args.moreSeatWindows;
};
